import classNames from 'classnames';
import React from 'react';

/*
 * Two defaults are deliberately NOT set here, despite looking like obvious wins:
 *
 * - `size` has no default. 14 call sites render a bare <Button> and bring their own
 *   padding via className (e.g. Auth/Login.jsx "text-base p-2"), and IconButton renders
 *   one too. Defaulting size would double their padding.
 * - `type` has no default. Playlists/Modal/Modal.jsx:437 pairs `form=` with no `type`
 *   and relies on the implicit type="submit" to save wizard step 1. Defaulting to
 *   "button" would silently break that submit.
 */

const sizes = {
  small: 'text-sm py-1 px-2.5',
  middle: 'text-sm py-2 px-3.5',
  // Console CTA metrics (5a): 13px/700, 10px 20px padding.
  cta: 'text-[13px] py-2.5 px-5',
};

/*
 * One view per intent. `primary` (the pre-console accent button), `white` and `promo` were
 * retired once their last call sites moved to `cta` and the danger tokens — an unused view
 * is how a design system grows five ways to say the same thing.
 */
const views = {
  // The accent CTA (Log in / Continue / Save & Sync). `border-transparent` neutralises the
  // base's border rather than changing the base, which the outlined views still need.
  cta: 'rounded-field border-transparent bg-ui-accent font-bold text-black hover:bg-ui-accent-hover',
  // Destructive confirm ("Remove device", "Delete account"): the CTA's geometry in the
  // filled danger colour, so a dialog footer reads as one control pair whichever action
  // it offers. `danger` below is the QUIET destructive (menu rows, inline actions).
  dangerCta: 'rounded-field border-transparent bg-ui-danger-solid font-bold text-white hover:bg-ui-danger-solid-hover',
  outline: 'text-ui-accent border-ui-accent hover:bg-ui-accent-wash',
  // Was `bg-gray-900 color-gray-700` — "color-gray-700" is not a Tailwind class, so the
  // text colour never applied.
  secondary: 'bg-ui-elevated text-ui-2 border-ui-hair-2 hover:bg-ui-overlay hover:text-ui',
  // `ghost` is passed at 9 call sites (Settings x2, Devices x6, Playlists modal) but was
  // absent from this map, so every one of them rendered unstyled.
  ghost: 'text-ui-3 hover:text-ui',
  danger: 'text-ui-danger hover:bg-ui-danger-wash',
};

/*
 * Disabled is its own state, not a faded copy of the resting one.
 *
 * `opacity-40` over the accent CTA produced a murky olive slab with black text on it: it
 * read as a rendering fault rather than as "not available yet", and the label fell to
 * roughly 2:1 against its own background. Instead every view resolves to one of three
 * treatments below, each of which keeps the label at >= 4.4:1 and keeps the button's
 * silhouette intact so the layout does not shift when it becomes available.
 *
 * Ordering: `:disabled` has specificity (0,2,0), so it outranks the plain view classes,
 * and Tailwind emits the `disabled:` variant AFTER `hover:` (default variant order), so
 * it also wins over the hover colours on browsers that still match :hover on a disabled
 * control. `cursor-not-allowed` rather than `pointer-events-none` — the latter would also
 * suppress the title tooltip that usually explains WHY the button is disabled.
 */
const DISABLED_FILLED = 'disabled:border-ui-hair-2 disabled:bg-ui-border disabled:text-ui-4 disabled:shadow-none';
const DISABLED_OUTLINE = 'disabled:border-ui-hair-2 disabled:bg-transparent disabled:text-ui-4';
const DISABLED_QUIET = 'disabled:border-transparent disabled:bg-transparent disabled:text-ui-4';

const disabledViews = {
  cta: DISABLED_FILLED,
  dangerCta: DISABLED_FILLED,
  secondary: DISABLED_FILLED,
  outline: DISABLED_OUTLINE,
  ghost: DISABLED_QUIET,
  danger: DISABLED_QUIET,
};

/*
 * The same recipe for hand-rolled controls that cannot be a <Button>: icon-only steppers,
 * inline save/cancel buttons, menu rows. Import this instead of inventing another
 * `disabled:opacity-*` — that is what produced five different disabled looks.
 */
export const DISABLED_CONTROL = `${DISABLED_QUIET} disabled:cursor-not-allowed`;
export const DISABLED_CONTROL_FILLED = `${DISABLED_FILLED} disabled:cursor-not-allowed`;

const alignment = {
  center: 'justify-center',
  left: 'justify-start',
};

export const Button = React.forwardRef(({
  className,
  children,
  size,
  truncate,
  block,
  view,
  icon,
  align,
  center,
  isDisabled,
  ...rest
}, ref) => {
  return (
    <button
      ref={ref}
      className={
        classNames(
          'inline-flex items-center rounded-tile border transition-colors duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
          'disabled:cursor-not-allowed',
          size && sizes[size],
          { 'truncate': truncate },
          { 'w-full': block },
          views[view],
          disabledViews[view] || DISABLED_QUIET,
          alignment[center ? 'center' : align],
          className,
        )}
      disabled={isDisabled}
      {...rest}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
});
Button.displayName = 'Button';

Button.defaultProps = {
  align: 'center',
};
